import {atom} from "jotai";
import {metlApi, type MetlApiBase} from "../../metl-api/MetlApiBase.ts";
import axios from "axios";
import {smartAtom} from "../../util/atom/SmartAtom.ts";
import axiosRetry from "axios-retry";
import axiosRateLimit from "axios-rate-limit";
import {createJSONStorage} from "jotai/utils";
import {axiosErrorInterceptor} from "../../metl-api/util/AxiosErrorInterceptor.ts";

export interface MetlConnectionProperties {
    baseUrl: string,
    auth: {
        username: string,
        password: string
    }
    options: {
        retry: number
        retryDelay: number
        rateLimit: number
    }
}

export const DefaultConnectionOptions: MetlConnectionProperties["options"] = {
    rateLimit: 20,
    retry: 3,
    retryDelay: 200
}

const storage = createJSONStorage<MetlConnectionProperties>(() => localStorage, {
    replacer: (key, value) => {
        if(key === "password") return undefined
        return value
    }
})

export const [ConnectionAtom, ConnectionAtomDep] = smartAtom<MetlConnectionProperties>({
    storageKey: "metl-connection",
    defaultValue: {
        baseUrl: "",
        auth: {
            username: "",
            password: ""
        },
        options: DefaultConnectionOptions
    },
    storage,
    sameCheck: (oldValue, newValue) => oldValue.baseUrl === newValue.baseUrl && oldValue.auth.username === newValue.auth.username
})

export function isConnectionValid({baseUrl, auth}: MetlConnectionProperties): boolean {
    return baseUrl !== "" && auth.username !== "" && auth.password !== "";
}

export function fixMetlUrl(url: string): string {
    //The url MUST end with /rest/v1/, so we need to add it if it's not there and we need to make sure it has the trailing slash
    if(url.endsWith("/rest/v1")) return url + "/"
    if(!url.endsWith("/rest/v1/")) {
        if(url.endsWith("/"))
            return url + "rest/v1/"
        return url + "/rest/v1/"
    }
    return url
}

export const ApiAtom = atom<MetlApiBase | undefined>((get) => {
    const connection = get(ConnectionAtom) 
    if(!isConnectionValid(connection)) return undefined
    let axiosInstance = axios.create({
        baseURL: fixMetlUrl(connection.baseUrl),
        auth: {
            username: connection.auth.username,
            password: connection.auth.password
        }
    })
    const options = connection.options ?? DefaultConnectionOptions
    if(options.retry !== -1) {
        axiosRetry(axiosInstance, {
            retries: options.retry,
        })
    }
    if(options.rateLimit !== -1) {
        axiosInstance = axiosRateLimit(axiosInstance, {
            maxRequests: options.rateLimit,
            perMilliseconds: 1000
        })
    }
    axiosInstance.interceptors.response.use(null, axiosErrorInterceptor)
    return metlApi(axiosInstance)
})